import { BehaviorSubject, forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, startWith, switchMap, take } from 'rxjs/operators';

import { ChangeDetectionStrategy, Component, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';

import { Store } from '@ngxs/store';

import { PricingDetailsComponent } from '@app/client/settings/shared/modules/subscription/components/pricing-details/pricing-details.component';
import { Toast } from '@app/core/models/common.models';
import {
  AdspertPlanType,
  Customer,
  CustomerSubscription,
  InvoiceCurrency,
  SubscriptionInvoicePeriod,
} from '@app/core/models/customer.models';
import { CustomerState } from '@app/core/store/states/customer/customer.state';
import { PaymentMethodsState } from '@app/core/store/states/customer/payment-methods/payment-methods.state';
import { StartCustomerSubscription } from '@app/core/store/states/customer/subscription/subscription-state.actions';
import { SubscriptionState } from '@app/core/store/states/customer/subscription/subscription.state';
import { DrawerInitDirective } from '@app/shared/directives/drawer-init.directive';
import { lastValue } from '@app/shared/operators/last-value.operator';
import { shareRef } from '@app/shared/operators/share-ref.operator';
import { BillingFormComponent } from '../../../billing/components/form/billing-form.component';
import { PaymentMethodSetupComponent } from '../../../payment-methods/components/setup/payment-method-setup.component';

@Component({
  standalone: false,
  selector: 'adspt-start-subscription-drawer',
  templateUrl: 'start-subscription-drawer.component.html',
  styleUrls: ['start-subscription-drawer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StartSubscriptionDrawerComponent extends DrawerInitDirective<boolean> {
  readonly toast$ = new Subject<Toast>();

  readonly customer$: Observable<Customer> = this.store.select(CustomerState.customer);

  readonly isSelfServiceLegacy$: Observable<boolean> = this.store.select(SubscriptionState.isSelfServiceLegacy);

  readonly previewSubscriptions$: Observable<CustomerSubscription[]> = this.store.select(SubscriptionState.preview);

  readonly hasPaymentMethods$: Observable<boolean> = this.store.select(PaymentMethodsState.hasPaymentMethods);

  @ViewChild(PricingDetailsComponent)
  set pricingDetails(form: PricingDetailsComponent) {
    this.pricingDetails$.next(form);
  }

  @ViewChild(BillingFormComponent)
  set billingForm(form: BillingFormComponent) {
    this.billingForm$.next(form);
  }

  @ViewChild(PaymentMethodSetupComponent)
  set paymentForm(form: PaymentMethodSetupComponent) {
    this.paymentForm$.next(form);
  }

  readonly billingForm$ = new BehaviorSubject<BillingFormComponent | undefined>(void 0);

  readonly paymentForm$ = new BehaviorSubject<PaymentMethodSetupComponent | undefined>(void 0);

  readonly pricingDetails$ = new BehaviorSubject<PricingDetailsComponent | undefined>(void 0);

  readonly billingFormValid$: Observable<boolean> = this.formComponentValid(this.billingForm$);

  readonly paymentFormValid$: Observable<boolean> = this.formComponentValid(this.paymentForm$);

  readonly pricingSelection$ = new BehaviorSubject<{
    plan: AdspertPlanType;
    currency?: InvoiceCurrency;
    period?: SubscriptionInvoicePeriod;
  } | null>(null);

  activeIdx = 0;

  constructor(private store: Store) {
    super();
  }

  drawerInit(): void {}

  private formComponentValid(subject: Observable<{ form$: Observable<FormGroup> }>): Observable<boolean> {
    return subject.pipe(
      switchMap((form) => (form ? form.form$ : of(void 0))),
      filter((form) => !!form),
      switchMap((form) =>
        form.statusChanges.pipe(
          startWith(form.status !== 'INVALID'),
          distinctUntilChanged(),
          map(() => form.valid),
        ),
      ),
      shareRef(),
    ) as Observable<boolean>;
  }

  readonly onPaymentMethodForm = (): Observable<boolean> => {
    return this.paymentForm$
      .getValue()
      .confirm()
      .pipe(
        switchMap((success) => (success ? this.startSubscription().pipe(map(() => true)) : of(false))),
        catchError(() => of(false)),
      );
  };

  readonly onSaveBillingForm = (): Observable<boolean> => {
    return forkJoin([this.billingForm$.getValue().submitForm(), this.hasPaymentMethods$.pipe(take(1))]).pipe(
      switchMap(([valid, hasPaymentMethods]) =>
        valid && hasPaymentMethods ? this.startSubscription().pipe(map(() => true)) : of(valid),
      ),
    );
  };

  readonly onSavePricingForm = () => {
    const pricingDetails = lastValue(this.pricingDetails$);
    const plan = lastValue(pricingDetails.planType$);
    const currency = lastValue(pricingDetails.invoiceCurrency$);
    const period = lastValue(pricingDetails.invoicePeriod$);

    this.pricingSelection$.next({ plan, currency, period });

    return of(true);
  };

  readonly onSavePricingLegacyForm = () => {
    const plan = this.store.selectSnapshot(SubscriptionState.planTypes)?.[0];

    this.pricingSelection$.next({ plan });

    return of(true);
  };

  startSubscription(): Observable<unknown> {
    const { plan, currency, period } = lastValue(this.pricingSelection$);

    return this.store.dispatch(new StartCustomerSubscription(plan, currency, period));
  }
}
